import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, ClipboardCheck, Wrench, Trash2, Pencil, CheckCircle2, Filter } from "lucide-react";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const CATEGORIES = [
  { value: "repair", label: "Perbaikan" },
  { value: "service", label: "Servis Berkala" },
  { value: "accident", label: "Kecelakaan" },
  { value: "inspection", label: "Inspeksi" },
  { value: "other", label: "Lainnya" },
];
const STATUS_LABEL = { in_progress: "Sedang Dikerjakan", completed: "Selesai", cancelled: "Dibatalkan" };
const STATUS_CLS = {
  in_progress: "bg-amber-100 text-amber-800 border-amber-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-slate-200 text-slate-700 border-slate-400",
};

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  vehicle_type: "truck",
  vehicle_id: "",
  category: "repair",
  description: "",
  mechanic_name: "",
  workshop_type: "internal",
  workshop_name: "",
  start_date: today(),
  end_date: "",
  cost: "",
  notes: "",
};

export default function Logbook() {
  const { user } = useAuth();
  const isKoor = user?.role === "koordinator";
  const canDelete = user?.role === "management" || user?.role === "admin";

  const [logs, setLogs] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [filter, setFilter] = useState({ status: "all", vehicle_type: "all" });
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [openClose, setOpenClose] = useState(false);
  const [closingLog, setClosingLog] = useState(null);
  const [closeForm, setCloseForm] = useState({ end_date: today(), cost: "", notes: "" });

  const load = () => {
    api.get("/maintenance-logs").then((r) => setLogs(r.data));
    api.get("/trucks").then((r) => setTrucks(r.data));
    api.get("/trailers").then((r) => setTrailers(r.data));
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 30000);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (filter.status !== "all" && l.status !== filter.status) return false;
      if (filter.vehicle_type !== "all" && l.vehicle_type !== filter.vehicle_type) return false;
      return true;
    });
  }, [logs, filter]);

  const activeCount = logs.filter((l) => l.status === "in_progress").length;

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpenForm(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      vehicle_type: l.vehicle_type,
      vehicle_id: l.vehicle_id,
      category: l.category,
      description: l.description,
      mechanic_name: l.mechanic_name,
      workshop_type: l.workshop_type,
      workshop_name: l.workshop_name || "",
      start_date: l.start_date,
      end_date: l.end_date || "",
      cost: l.cost ?? "",
      notes: l.notes || "",
    });
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.end_date) delete payload.end_date;
      if (payload.cost === "" || payload.cost === null) delete payload.cost;
      else payload.cost = Number(payload.cost);
      if (editing) {
        await api.patch(`/maintenance-logs/${editing.id}`, payload);
        toast.success("Log diperbarui");
      } else {
        await api.post("/maintenance-logs", payload);
        toast.success("Log ditambahkan");
      }
      setOpenForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (l) => {
    if (!confirm(`Hapus log "${l.description}"? Jika ada biaya tercatat akan ikut dihapus.`)) return;
    try { await api.delete(`/maintenance-logs/${l.id}`); toast.success("Log dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openCloseDialog = (l) => {
    setClosingLog(l);
    setCloseForm({ end_date: today(), cost: l.cost ?? "", notes: l.notes || "" });
    setOpenClose(true);
  };

  const submitClose = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { end_date: closeForm.end_date, notes: closeForm.notes };
      if (closeForm.cost !== "" && closeForm.cost !== null) payload.cost = Number(closeForm.cost);
      await api.post(`/maintenance-logs/${closingLog.id}/close`, payload);
      toast.success("Log ditutup — biaya otomatis dicatat");
      setOpenClose(false);
      setClosingLog(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const vehicleOptions = form.vehicle_type === "truck"
    ? trucks.map((t) => ({ id: t.id, label: t.plat_nomor }))
    : trailers.map((t) => ({ id: t.id, label: t.code }));

  return (
    <div>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-orange-500" />
            Logbook Maintenance
          </h1>
          <p className="text-slate-500 text-sm mt-1">Catatan perbaikan, servis & kondisi armada.</p>
        </div>
        <Button onClick={openAdd} data-testid="add-log-btn" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
          <Plus className="w-4 h-4 mr-1" /> Tambah Log
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Total Log" value={logs.length} accent="bg-slate-900 text-orange-400" testId="summary-total" />
        <SummaryCard label="Sedang Dikerjakan" value={activeCount} accent="bg-amber-500 text-slate-900" testId="summary-active" />
        <SummaryCard label="Selesai" value={logs.filter((l) => l.status === "completed").length} accent="bg-emerald-500 text-white" testId="summary-completed" />
        <SummaryCard label="Unit di Maintenance" value={new Set(logs.filter((l) => l.status === "in_progress").map((l) => l.vehicle_id)).size} accent="bg-orange-500 text-slate-900" testId="summary-units" />
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-100 border-2 border-slate-200 rounded-sm">
        <Filter className="w-4 h-4 text-slate-500" />
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="h-9 w-48 rounded-sm bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="in_progress">Sedang Dikerjakan</SelectItem>
            <SelectItem value="completed">Selesai</SelectItem>
            <SelectItem value="cancelled">Dibatalkan</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.vehicle_type} onValueChange={(v) => setFilter({ ...filter, vehicle_type: v })}>
          <SelectTrigger className="h-9 w-48 rounded-sm bg-white" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Unit</SelectItem>
            <SelectItem value="truck">Head Truck</SelectItem>
            <SelectItem value="trailer">Trailer / GTM</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-500 ml-auto font-mono">{filtered.length} log</div>
      </div>

      {/* Table */}
      <div className="border-2 border-slate-200 rounded-sm bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Tanggal</TableHead>
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Unit</TableHead>
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Kategori</TableHead>
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Deskripsi</TableHead>
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Mekanik</TableHead>
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs">Status</TableHead>
              {!isKoor && <TableHead className="text-orange-400 uppercase tracking-wider text-xs text-right">Biaya</TableHead>}
              <TableHead className="text-orange-400 uppercase tracking-wider text-xs text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={isKoor ? 7 : 8} className="text-center py-10 text-slate-500">
                  Belum ada log maintenance. Klik <b>Tambah Log</b> untuk mulai.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`log-row-${l.id}`} className="hover:bg-slate-50">
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  <div>{l.start_date}</div>
                  {l.end_date && <div className="text-slate-400">→ {l.end_date}</div>}
                </TableCell>
                <TableCell>
                  <div className="font-mono font-bold">{l.vehicle_label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{l.vehicle_type === "truck" ? "Head Truck" : "Trailer/GTM"}</div>
                </TableCell>
                <TableCell className="text-xs">{CATEGORIES.find((c) => c.value === l.category)?.label || l.category}</TableCell>
                <TableCell className="max-w-xs">
                  <div className="text-sm font-medium">{l.description}</div>
                  {l.workshop_name && <div className="text-xs text-slate-500">📍 {l.workshop_name} ({l.workshop_type === "internal" ? "Internal" : "Eksternal"})</div>}
                  {l.notes && <div className="text-xs text-slate-400 italic mt-0.5">{l.notes}</div>}
                </TableCell>
                <TableCell className="text-sm">{l.mechanic_name}</TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase ${STATUS_CLS[l.status]}`}>
                    {STATUS_LABEL[l.status]}
                  </span>
                </TableCell>
                {!isKoor && (
                  <TableCell className="text-right font-mono">
                    {l.cost != null ? formatIDR(l.cost) : <span className="text-slate-300">—</span>}
                  </TableCell>
                )}
                <TableCell className="text-right whitespace-nowrap">
                  {l.status === "in_progress" && (
                    <Button size="sm" onClick={() => openCloseDialog(l)} data-testid={`close-log-${l.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 rounded-sm mr-1">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Selesaikan
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEdit(l)} data-testid={`edit-log-${l.id}`} className="h-7 px-2 rounded-sm border-slate-300 mr-1">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {canDelete && (
                    <Button variant="outline" size="sm" onClick={() => del(l)} data-testid={`delete-log-${l.id}`} className="h-7 px-2 rounded-sm border-red-300 text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="rounded-sm border-2 border-slate-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <Wrench className="w-6 h-6 text-orange-500" />
              {editing ? "Edit Log Maintenance" : "Tambah Log Maintenance"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tipe Unit *</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v, vehicle_id: "" })}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid="log-vehicle-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Head Truck</SelectItem>
                    <SelectItem value="trailer">Trailer / GTM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Unit *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid="log-vehicle-id"><SelectValue placeholder="— pilih —" /></SelectTrigger>
                  <SelectContent>
                    {vehicleOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Kategori *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid="log-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Mekanik *</Label>
                <Input required data-testid="log-mechanic" placeholder="Nama mekanik" value={form.mechanic_name} onChange={(e) => setForm({ ...form, mechanic_name: e.target.value })} className="h-11 rounded-sm" />
              </div>
            </div>
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Deskripsi Pekerjaan *</Label>
              <Textarea required data-testid="log-description" placeholder="Contoh: Ganti kampas rem depan, servis rutin 10.000 km, dst" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tipe Bengkel</Label>
                <Select value={form.workshop_type} onValueChange={(v) => setForm({ ...form, workshop_type: v })}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid="log-workshop-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal (Garasi Sendiri)</SelectItem>
                    <SelectItem value="external">Eksternal (Bengkel Luar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Nama Bengkel</Label>
                <Input data-testid="log-workshop-name" placeholder="opsional" value={form.workshop_name} onChange={(e) => setForm({ ...form, workshop_name: e.target.value })} className="h-11 rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tanggal Mulai *</Label>
                <Input required type="date" data-testid="log-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11 rounded-sm" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tanggal Selesai</Label>
                <Input type="date" data-testid="log-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-11 rounded-sm" />
                <p className="text-[10px] text-slate-500 mt-1">Kosongkan jika masih berjalan</p>
              </div>
            </div>
            {!isKoor && (
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Biaya (Rp)</Label>
                <Input type="number" min="0" step="1000" data-testid="log-cost" placeholder="Kosongkan jika belum ada" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="h-11 rounded-sm font-mono" />
                <p className="text-[10px] text-slate-500 mt-1">Otomatis masuk Riwayat Biaya kendaraan saat log ditutup / selesai.</p>
              </div>
            )}
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Catatan</Label>
              <Textarea data-testid="log-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(false)} className="rounded-sm">Batal</Button>
              <Button type="submit" disabled={busy} data-testid="log-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                {busy ? "..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={openClose} onOpenChange={setOpenClose}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              Selesaikan Log
            </DialogTitle>
          </DialogHeader>
          {closingLog && (
            <form onSubmit={submitClose} className="space-y-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm text-sm">
                <div className="font-bold">{closingLog.vehicle_label} · {CATEGORIES.find((c) => c.value === closingLog.category)?.label}</div>
                <div className="text-slate-600">{closingLog.description}</div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tanggal Selesai *</Label>
                <Input required type="date" data-testid="close-end-date" value={closeForm.end_date} onChange={(e) => setCloseForm({ ...closeForm, end_date: e.target.value })} className="h-11 rounded-sm" />
              </div>
              {!isKoor && (
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Biaya Final (Rp)</Label>
                  <Input type="number" min="0" step="1000" data-testid="close-cost" value={closeForm.cost} onChange={(e) => setCloseForm({ ...closeForm, cost: e.target.value })} className="h-11 rounded-sm font-mono" />
                  <p className="text-[10px] text-slate-500 mt-1">Jika diisi, otomatis masuk Riwayat Biaya kendaraan.</p>
                </div>
              )}
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Catatan</Label>
                <Textarea data-testid="close-notes" rows={2} value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenClose(false)} className="rounded-sm">Batal</Button>
                <Button type="submit" disabled={busy} data-testid="close-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase rounded-sm">
                  {busy ? "..." : "Selesaikan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div className="bg-white border-2 border-slate-200 p-4 rounded-sm" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
          <div className="font-display text-3xl font-black text-slate-900 mt-1">{value}</div>
        </div>
        <div className={`w-8 h-8 flex items-center justify-center ${accent}`}>
          <Wrench className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}
